import { GameHintStage } from 'enums/GameHintStage';
import { GameMenuState } from 'enums/GameMenuState';
import { StorageKey } from 'enums/StorageKey';
import { Action } from 'redux';

import {
    GameActionType,
    SetGameMenuState,
    SetGameOverAction,
    SetGameScoreAction,
    SetGameVolumeAction,
    SetShowGameStarterAction,
} from './game.actions';

const getStoredHintStage = () => {
    const storedFinished = localStorage.getItem(StorageKey.GameHintsFinished);

    if (storedFinished && JSON.parse(storedFinished) === true) {
        return GameHintStage.Finished;
    }

    return GameHintStage.NotStarted;
};

const getHintsActive = (hintStage: GameHintStage) =>
    hintStage > GameHintStage.NotStarted && hintStage < GameHintStage.Finished;

const getHintsFinished = (hintStage: GameHintStage) => hintStage >= GameHintStage.Finished;

const initialHintStage = getStoredHintStage();

const initialState = {
    running: false,
    paused: false,
    volume: 0,
    over: false,
    stats: { score: 0, duration: 0 } as { score: number; duration: number },
    showStarter: false,
    starterWasShown: false,
    hintStage: initialHintStage,
    hintsActive: getHintsActive(initialHintStage),
    hintsFinished: getHintsFinished(initialHintStage),
    gameMenuState: GameMenuState.None,
};

export const gameReducer = (
    state = initialState,
    action: Action<GameActionType>,
): typeof initialState => {
    switch (action.type) {
        case GameActionType.Start:
        case GameActionType.Stop: {
            const { volume } = state;
            const hintStage = getStoredHintStage();
            const hintsActive = getHintsActive(hintStage);
            const hintsFinished = getHintsFinished(hintStage);
            return {
                ...initialState,
                volume,
                hintStage,
                hintsActive,
                hintsFinished,
                running: action.type === GameActionType.Start,
            };
        }

        case GameActionType.Pause:
            return { ...state, paused: true };

        case GameActionType.Resume:
            return { ...state, paused: false };

        case GameActionType.SetGameMenuState:
            const gameMenuState = (action as SetGameMenuState).payload;
            return { ...state, gameMenuState };

        case GameActionType.SetShowStarter:
            const showStarter = (action as SetShowGameStarterAction).payload;
            const starterWasShown = state.starterWasShown || showStarter;
            return { ...state, showStarter, starterWasShown };

        case GameActionType.ResetStarterWasShown:
            return { ...state, starterWasShown: false };

        case GameActionType.SetScore:
            const { score, duration } = (action as SetGameScoreAction).payload;
            return { ...state, stats: { score, duration } };

        case GameActionType.SetVolume: {
            const volume = (action as SetGameVolumeAction).payload;
            return { ...state, volume };
        }

        case GameActionType.SetOver:
            const over = (action as SetGameOverAction).payload;
            return { ...state, over };

        case GameActionType.StartHints: {
            const hintStage = 1 as GameHintStage;
            const hintsActive = getHintsActive(hintStage);
            const hintsFinished = getHintsFinished(hintStage);
            return { ...state, hintStage, hintsActive, hintsFinished };
        }

        case GameActionType.NextHint: {
            const hintStage = Math.min(state.hintStage + 1, GameHintStage.Finished);
            const hintsActive = getHintsActive(hintStage);
            const hintsFinished = getHintsFinished(hintStage);
            return { ...state, hintStage, hintsActive, hintsFinished };
        }

        default:
            return state;
    }
};
